import { ReadonlyVec2, vec2 } from 'gl-matrix';
import { gl } from '../../Engine';
import assert from 'assert';

type RenderTargetContentType = WebGLTexture | WebGLBuffer;

/**
 * FrameBufferObject offers an interface on top of the WebGL to create and manage FrameBuffers.
 *
 * FrameBuffers work as alternate render destinations,
 * that allows you to perform rendering operations off-screen, into a separate buffer.
 * FrameBuffers are collections of RenderTargets (also known as Attachments) that may be modified by rendering functions.
 * Those RenderTargets may be either Textures or Buffers.
 *
 * FrameBufferObject contains a Map of RenderTargets,
 * the lifecycle of which will be ended at the same time with the FrameBufferObject.
 * One may also bind a texture to the FrameBufferObject without adding it to that map,
 * using createTextureLayer(texture, layer, attachment); the caller then remains
 * responsible for deallocating that texture when it is no longer needed.
 */
export class FrameBufferObject {
    /**
     * Size of the FrameBufferObject in pixels
     */
    private _size: vec2;
    /**
     * Reference to the WebGL Framebuffer object
     */
    public readonly id: WebGLFramebuffer;

    /**
     * Map containing renderTargets/attachments stored inside this FrameBufferObject.
     * All the renderTargets stored inside the FrameBufferObject at the time of FBO delete are also deleted.
     *
     * To add RenderTargets to this map, one has to use createTextureAttachment/createBufferAttachment -methods.
     *
     * The keys in this map are the attachments and
     * values are the RenderTargets.
     */
    private renderTargets: Map<number, RenderTarget<RenderTargetContentType>>;

    /**
     * FrameBufferObject constructor with a single size parameter.
     * size.x and size.y should be whole numbers.
     * @throws if either of the size components are not integer type
     * @param size The size of this FrameBufferObject in pixels
     */
    constructor(size: ReadonlyVec2 | number) {
        if (typeof size === 'number') {
            size = vec2.fromValues(size, size);
        }
        assert(
            Number.isInteger(size[0]),
            `FrameBufferObject size components must be whole numbers, received x-coordinate ${size[0]}.`
        );
        assert(
            Number.isInteger(size[1]),
            `FrameBufferObject size components must be whole numbers, received y-coordinate ${size[1]}.`
        );
        assert(
            size[0] > 0,
            `FrameBufferObject size components must be positive, received negative x-coordinate ${size[0]}.`
        );
        assert(
            size[1] > 0,
            `FrameBufferObject size components must be positive, received negative x-coordinate ${size[1]}.`
        );
        this._size = vec2.clone(size);
        const frameBuffer = gl.createFramebuffer();
        if (!frameBuffer) {
            throw new Error('');
        }
        this.id = frameBuffer;
        this.renderTargets = new Map<
            number,
            RenderTarget<RenderTargetContentType>
        >();
        this.bind();
    }

    /**
     * Method to bind this FrameBufferObject to WebGL context.
     * This makes the FrameBufferObject as the currently active render target.
     * Remember to also set the gl.viewport() to match the size of this FrameBufferObject if it happens to differ.
     */
    public bind(): void {
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.id);
    }

    /**
     * Method to unbind this FrameBufferObject on the WebGL context.
     * Technically it doesn't matter whether this is currently active or not.
     * Sets the currently active FrameBuffer to null,
     * which causes the subsequent render calls to manipulate the default FrameBufferObject,
     * which is the canvas.
     * Depending on what you are doing, you might need to update the gl.viewport() to make sure the rendering area is correct.
     */
    public unbind(): void {
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }

    /**
     * Method to resize the FrameBufferObject.
     * This also updates the size of all the RenderTargets inside the renderTargets map.
     * Which also clears any previous data stored on those RenderTargets.
     *
     * If the new size equals FrameBufferObject's previous size, does nothing.
     * @throws if either of the size components is not integer
     * @param size FrameBufferObject's new size in pixels
     * @return reference to this FrameBufferObject
     */
    public resize(size: ReadonlyVec2): FrameBufferObject {
        if (vec2.equals(size, this._size)) {
            return this;
        }
        assert(
            Number.isInteger(size[0]),
            `FrameBufferObject size components must be whole numbers, received x-coordinate ${size[0]}.`
        );
        assert(
            Number.isInteger(size[1]),
            `FrameBufferObject size components must be whole numbers, received y-coordinate ${size[1]}.`
        );
        assert(
            size[0] > 0,
            `FrameBufferObject size components must be positive, received negative x-coordinate ${size[0]}.`
        );
        assert(
            size[1] > 0,
            `FrameBufferObject size components must be positive, received negative x-coordinate ${size[1]}.`
        );
        vec2.copy(this._size, size);
        this.bind();
        this.renderTargets.forEach(
            (renderTarget: RenderTarget<RenderTargetContentType>) => {
                renderTarget.resize(this._size);
            }
        );
        return this;
    }

    /**
     * Method to create a new RenderTarget with texture attachment under this FrameBufferObject.\
     * This creates both the texture as well as the necessary linking between the RenderTarget and this FrameBufferObject.
     *
     * The texture held by this TextureAttachment is erased at the same time with this FrameBufferObject,\
     * unless removed from the FrameBufferObject.
     *
     * If the attachment already has other RenderTarget stored in it, that old RenderTarget is deleted and replaced.\
     * For an exhaustive list of possible parameter values see https://developer.mozilla.org/en-US/docs/Web/API/WebGLRenderingContext/framebufferTexture2D
     * and https://developer.mozilla.org/en-US/docs/Web/API/WebGLRenderingContext/texImage2D.
     * Note: Remember to bind the {@link FrameBufferObject} before calling this method.
     * @param attachment The attachment which this TextureAttachment should link to. Possible values include:
     * {@link WebGL2RenderingContext.COLOR_ATTACHMENT0 gl.COLOR_ATTACHMENT0}, {@link WebGL2RenderingContext.DEPTH_ATTACHMENT gl.DEPTH_ATTACHMENT},
     * {@link WebGL2RenderingContext.STENCIL_ATTACHMENT gl.STENCIL_ATTACHMENT}, {@link WebGL2RenderingContext.DEPTH_STENCIL_ATTACHMENT gl.DEPTH_STENCIL_ATTACHMENT} and their derivatives.
     * @param internalFormat The internal format of the texture, such as
     * {@link WebGL2RenderingContext.RGB gl.RGB}, {@link WebGL2RenderingContext.RGBA gl.RGBA},
     * {@link WebGL2RenderingContext.R8 gl.R8} or {@link WebGL2RenderingContext.DEPTH_COMPONENT gl.DEPTH_COMPONENT}
     * @param format The format of the texture data. Table of possible combinations can be found at
     * https://registry.khronos.org/webgl/specs/latest/2.0/#TEXTURE_TYPES_FORMATS_FROM_DOM_ELEMENTS_TABLE
     * @param type The type of the texture data. Such as gl.UNSIGNED_BYTE, gl.INT or gl.FLOAT
     * @returns Reference to the TextureRenderTarget that was created.
     */
    public createTextureAttachment(
        attachment: number,
        internalFormat: number,
        format: number,
        type: number
    ): TextureRenderTarget {
        const old = this.renderTargets.get(attachment);
        if (old) {
            old.delete();
        }
        const target = new TextureRenderTarget(
            this.size,
            attachment,
            internalFormat,
            format,
            type
        );
        this.renderTargets.set(attachment, target);
        return target;
    }

    /**
     * Method to create a new RenderTarget with buffer attachment under this FrameBufferObject.
     * This creates both the buffer as well as the necessary linking between the RenderTarget and this FrameBufferObject.
     *
     * The buffer held by this BufferAttachment is erased at the same time with this FrameBufferObject,
     * unless removed from the FrameBufferObject.
     *
     * If the attachment already has other RenderTarget stored in it, that old RenderTarget is deleted and replaced.
     * For an exhaustive list of possible parameter values see https://developer.mozilla.org/en-US/docs/Web/API/WebGLRenderingContext/framebufferRenderbuffer
     * and https://developer.mozilla.org/en-US/docs/Web/API/WebGLRenderingContext/renderbufferStorage
     * @param attachment The attachment which this RenderBufferAttachment should link to. Possible values include:
     *  gl.COLOR_ATTACHMENT0, gl.DEPTH_ATTACHMENT, gl.STENCIL_ATTACHMENT, gl.DEPTH_STENCIL_ATTACHMENT and their derivatives.
     * @param internalFormat The internal format of the RenderBuffer, such as gl.R8 or gl.DEPTH_COMPONENT24
     *
     * @returns Reference to the BufferRenderTarget that was created.
     */
    public createBufferAttachment(
        attachment: number,
        internalFormat: number
    ): BufferRenderTarget {
        const target = new BufferRenderTarget(
            this.size,
            attachment,
            internalFormat
        );
        this.renderTargets.set(attachment, target);
        return target;
    }

    /**
     * Method to bind a single layer of a 3D texture to FrameBufferObject
     * @param texture The texture to bind
     * @param layer layer index number. Whole number [0, texture.size.z - 1]
     * @param attachment The attachment to which the texture should be bound. Defaults to gl.COLOR_ATTACHMENT0.
     * Possible values are gl.COLOR_ATTACHMENT{0-15}, gl.DEPTH_ATTACHMENT, gl.STENCIL_ATTACHMENT and gl.DEPTH_STENCIL_ATTACHMENT
     */
    public createTextureLayer(
        texture: WebGLTexture,
        layer: number,
        attachment: number = gl.COLOR_ATTACHMENT0
    ) {
        gl.framebufferTextureLayer(
            gl.FRAMEBUFFER,
            attachment,
            texture,
            0,
            layer
        );
    }

    /**
     * Width of this FrameBufferObject in pixels
     */
    public get width(): number {
        return this._size[0];
    }

    /**
     * Height of this FrameBufferObject in pixels
     */
    public get height(): number {
        return this._size[1];
    }

    /**
     * Size of this FrameBufferObject in pixels
     */
    public get size(): vec2 {
        return this._size;
    }

    /**
     * Method to bind the data of one of this FrameBufferObject's RenderTargets.
     * This binds the underlying data. In case of TextureRenderTarget it binds the texture
     * and in case of BufferRenderTarget it binds the RenderBuffer.
     * That data can then be accessed within FragmentShader for example.
     * @throws If FrameBufferObject contains no RenderTarget for the specified attachment
     * @param attachment Attachment the RenderTarget of which is to be bound
     * @returns True if the RenderTarget was successfully bound, false otherwise
     */
    public bindRenderTarget(attachment: number): boolean {
        const target = this.renderTargets.get(attachment);
        if (target) {
            return target.bind();
        }
        throw new Error(
            `Attempting to bind non-existing render target at attachment ${attachment}.`
        );
    }

    /**
     * Method to delete the FrameBufferObject and all the RenderTargets held within.
     * This is exhaustive deletion. So also the attachments of those RenderTargets are erased.
     * Meaning the texture and renderbuffer objects.
     */
    public delete(): void {
        this.renderTargets.forEach((r) => {
            r.delete();
        });
        this.renderTargets.clear();
        gl.deleteFramebuffer(this.id);
    }

    /**
     * Method to verify the status of the FrameBufferObject.
     * It is adviced to always check the status of FrameBufferObject after creating new RenderTargets
     * as this checks the validity of those RenderTargets.
     * And makes sure the FrameBufferObject can be used for rendering.
     * @throws An error if the FrameBuffer is not valid
     * @returns true if the FrameBufferObject and RenderTargets are valid. False otherwise.
     */
    public checkStatus(): boolean {
        const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
        if (status === 0) {
            throw new Error(`FrameBuffer got an error:\n${status}`);
        } else if (status !== gl.FRAMEBUFFER_COMPLETE) {
            throw new Error(`FrameBuffer status ${status}`);
            return false;
        }
        return true;
    }
}

/**
 * Abstract class defining the methods RenderTargets should have.
 * Generic type defines the type of the underlying WebGL data
 */
export abstract class RenderTarget<RenderTargetContentType> {
    /**
     * The underlying content. WebGLTexture in case of
     */
    protected id: RenderTargetContentType;

    /**
     * Attachment within the FrameBufferObject to which this RenderTarget should attach itself to
     */
    protected readonly attachment: number;

    /**
     * All RenderTargets should contain data of their specified type
     * @param id Reference to that underlying WebGL data
     */
    protected constructor(id: RenderTargetContentType, attachment: number) {
        this.id = id;
        this.attachment = attachment;
    }

    /**
     * Method to delete the underlying WebGL data
     */
    public abstract delete(): void;

    /**
     * Method to bind the underlying WebGL data
     */
    public abstract bind(): boolean;

    /**
     * Method to resize the RenderTarget using 2D vector as a parameter.
     * @param size Vector containing the desired width and height in pixels.
     * Both vector components should be whole numbers.
     * @returns Reference to RenderTarget
     */
    public abstract resize(size: vec2): RenderTarget<RenderTargetContentType>;
}

/**
 * RenderBuffer is a type of object that is used for efficient off-screen rendering.
 * It is a storage container for pixel data that can be directly rendered into, without being displayed on the screen.
 *
 * A RenderBuffer is essentially a block of memory allocated by the GPU,
 * which can store pixel data in a specific format,
 * such as color values, depth information, or stencil values.
 * It is optimized for rendering and can be more efficient than other types of storage, such as textures.
 *
 * RenderBuffers are primarily used for intermediate render targets,
 * where the rendered data does not need to be accessed by the CPU or displayed directly to the screen.
 */
export class BufferRenderTarget extends RenderTarget<WebGLBuffer> {
    /**
     * The internal format of the RenderBuffer, such as gl.R8 or gl.DEPTH_COMPONENT24.
     * For exhaustive list of all possible values, refer to https://developer.mozilla.org/en-US/docs/Web/API/WebGLRenderingContext/renderbufferStorage
     */
    private readonly internalFormat: number;

    /**
     * Constructor for RenderTarget with RenderBuffer as its data storage.
     * @throws Error if either of the size vector components are not whole numbers or WebGL fails to a create RenderBuffer.
     * @param size The size of this RenderTarget in pixels, must be whole numbers
     * @param attachment The attachment in FrameBufferObject which this RenderTarget should attach itself to.
     * One of gl.COLOR_ATTACHMENT{1-15}, gl.DEPTH_ATTACHMENT, gl.STENCIL_ATTACHMENT or gl.DEPTH_STENCIL_ATTACHMENT
     * @param internalFormat The internal format of the RenderBuffer, such as gl.R8 or gl.DEPTH_COMPONENT24.
     * For exhaustive list of all possible values, refer to https://developer.mozilla.org/en-US/docs/Web/API/WebGLRenderingContext/renderbufferStorage
     */
    public constructor(size: vec2, attachment: number, internalFormat: number) {
        if (!Number.isInteger(size[0]) || !Number.isInteger(size[1])) {
            throw new Error(
                `Attempt to create RenderBuffer with size ${size} whose vector components are not integers.`
            );
        }
        const renderBuffer = gl.createRenderbuffer();
        if (!renderBuffer) {
            throw new Error('RenderBuffer creation failed');
        }
        super(renderBuffer, attachment);
        this.internalFormat = internalFormat;
        this.createStorage(size);
    }

    /**
     * Method that will initialize a RenderBuffer of given size
     * @param size The size of the renderBuffer in pixels. Must be whole numbers.
     */
    private createStorage(size: vec2) {
        if (this.id) {
            gl.bindRenderbuffer(gl.RENDERBUFFER, this.id);
            gl.renderbufferStorage(
                gl.RENDERBUFFER,
                this.internalFormat,
                size[0],
                size[1]
            );
            gl.framebufferRenderbuffer(
                gl.FRAMEBUFFER,
                this.attachment,
                gl.RENDERBUFFER,
                this.id
            );
        } else {
            throw new Error(
                "Failed creating RenderBuffer as FBO's render target"
            );
        }
    }

    /**
     * Method to delete the RenderBuffer
     */
    public delete(): void {
        gl.deleteRenderbuffer(this.id);
    }

    /**
     * Method to bind the render buffer.
     * This is not usually needed as RenderBuffers cannot be read to CPU memory directly.
     * @returns True if the RenderTarget was successfully bound
     */
    public bind(): boolean {
        gl.bindRenderbuffer(gl.RENDERBUFFER, this.id);
        return true;
    }

    public resize(size: vec2): BufferRenderTarget {
        this.delete();
        const buffer = gl.createRenderbuffer();
        if (!buffer) {
            throw new Error('RenderBuffer creation failed');
        }
        this.id = buffer;
        this.createStorage(size);
        return this;
    }
}

/**
 * RenderTarget with texture content used for off-screen rendering.
 * Storage container for pixel data that can be directly rendered into, with possibility to be also displayed on the screen.
 *
 * TextureRenderTarget may contain color, depth or stencil data that can be also read to CPU or used in subsequent
 * render calls as an input data.
 *
 * Rendering data to Texture attachment is possibly less efficient than using RenderBuffers,
 * but the data may be used in subsequent render calls as a data input or read to CPU directly,
 * which is something RenderBuffers cannot be used for.
 */
export class TextureRenderTarget extends RenderTarget<WebGLTexture> {
    /**
     * The internal format of the texture, such as gl.RGB, gl.RGBA, gl.R8 or gl.DEPTH_COMPONENT.
     * For exhaustive list of possible values, see https://developer.mozilla.org/en-US/docs/Web/API/WebGLRenderingContext/texImage2D
     */
    private readonly internalFormat: number;

    /**
     * The format of the texture data. Table of possible combinations can be found at
     * https://registry.khronos.org/webgl/specs/latest/2.0/#TEXTURE_TYPES_FORMATS_FROM_DOM_ELEMENTS_TABLE
     */
    private readonly format: number;

    /**
     * The type of the texture data. Such as gl.UNSIGNED_BYTE, gl.INT or gl.FLOAT
     */
    private readonly type: number;

    /**
     * Constructor that will create a Texture and link it to a FrameBufferObject
     * @throws if Texture creation fails
     * @param size The size of the FrameBufferObject and the texture in pixels. Both vector components must be whole numbers.
     * @param attachment FrameBufferObject's attachment this TextureRenderTarget should be bound to
     * One of gl.COLOR_ATTACHMENT{1-15}, gl.DEPTH_ATTACHMENT, gl.STENCIL_ATTACHMENT or gl.DEPTH_STENCIL_ATTACHMENT
     * @param internalFormat The underlying texture's internal format.
     * Exhaustive list of possible values can be found at https://developer.mozilla.org/en-US/docs/Web/API/WebGLRenderingContext/texImage2D
     * @param format The format of the texture data. Table of possible combinations can be found at
     * https://registry.khronos.org/webgl/specs/latest/2.0/#TEXTURE_TYPES_FORMATS_FROM_DOM_ELEMENTS_TABLE
     * @param type The type of the texture data. Such as gl.UNSIGNED_BYTE, gl.INT or gl.FLOAT
     */
    public constructor(
        size: vec2,
        attachment: number,
        internalFormat: number,
        format: number,
        type: number
    ) {
        const texture = gl.createTexture();
        if (!texture) {
            throw new Error('Texture creation for TextureRenderTarget failed');
        }
        super(texture, attachment);
        this.internalFormat = internalFormat;
        this.format = format;
        this.type = type;
        this.createTexture(size);
    }

    /**
     * Private constructor that will initialize the texture attachment.
     * At some point we might want to introduce methods/means to alter the texture parameters such as wrapping and filters.
     * Currently this uses linear sampling and clamps to edge, which is the most commonly used approach.
     * @param size The desired texture size in pixels. Both vector components should be whole numbers.
     */
    private createTexture(size: vec2): void {
        gl.bindTexture(gl.TEXTURE_2D, this.id);
        gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            this.internalFormat,
            size[0],
            size[1],
            0,
            this.format,
            this.type,
            null
        );
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.framebufferTexture2D(
            gl.FRAMEBUFFER,
            this.attachment,
            gl.TEXTURE_2D,
            this.id,
            0
        );
    }

    /**
     * Method to delete the TextureRenderTarget. This will de-allocate the underlying WebGLTexture-object
     */
    public delete(): void {
        gl.deleteTexture(this.id);
    }

    /**
     * Method to bind the underlying WebGLTexture-object
     * @returns True to indicate that the texture binding was successful
     */
    public bind(): boolean {
        gl.bindTexture(gl.TEXTURE_2D, this.id);
        return true;
    }

    /**
     * Method to resize this TextureRenderTarget.
     * This replaces the texture with a new texture of requested size.
     * @throws if Texture creation failed
     * @param size The desired texture size in pixels. Both vector components must be whole numbers
     * @returns Reference to this TextureRenderTarget
     */
    public resize(size: vec2): TextureRenderTarget {
        this.delete();
        const texture = gl.createTexture();
        if (!texture) {
            throw new Error('');
        }
        this.id = texture;
        this.createTexture(size);
        return this;
    }

    public getTexture(): WebGLTexture {
        return this.id;
    }
}
